import type {
  IStyleCatalogPreset,
  IStyleCatalogResponse,
} from '@/interfaces/IStorefrontStyle';
import { isStyleMode } from './design-draft';

/** The rule the API applies to `preset` (D-TH-4). */
const PRESET_ID = /^[a-z][a-z0-9-]{1,31}$/;

export interface IStylePresetOption {
  id: string;
  name: string;
  hint?: string;
  defaultMode: IStyleCatalogPreset['defaultMode'];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePreset(value: unknown): IStyleCatalogPreset | null {
  if (!isRecord(value)) return null;
  const { id, name, defaultMode } = value;
  if (typeof id !== 'string' || !PRESET_ID.test(id)) return null;
  if (typeof name !== 'string' || name.trim() === '') return null;
  if (typeof defaultMode !== 'string' || !isStyleMode(defaultMode)) return null;
  return { id, name: name.trim(), defaultMode };
}

/** Keeps the well-formed presets (first occurrence of an id wins); null when none is left or the shape is wrong. */
export function parseStyleCatalog(data: unknown): IStyleCatalogResponse | null {
  if (!isRecord(data) || !Array.isArray(data.presets)) return null;
  const { engine, defaultPresetId } = data;
  if (typeof engine !== 'number' || !Number.isFinite(engine)) return null;
  if (typeof defaultPresetId !== 'string') return null;
  const presets: IStyleCatalogPreset[] = [];
  for (const entry of data.presets) {
    const preset = parsePreset(entry);
    if (preset && !presets.some((known) => known.id === preset.id))
      presets.push(preset);
  }
  return presets.length ? { engine, defaultPresetId, presets } : null;
}

/** Admin labels win (`design.style.<id>.name|hint`); the catalog name is the fallback, no hint then. */
export function presetOptions(
  catalog: IStyleCatalogResponse,
  label: (id: string) => { name?: string; hint?: string }
): IStylePresetOption[] {
  return catalog.presets.map((preset) => {
    const own = label(preset.id);
    return {
      id: preset.id,
      name: own.name ?? preset.name,
      hint: own.hint,
      defaultMode: preset.defaultMode,
    };
  });
}
